import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { siteGetSites, siteCreateSite, siteUpdateSite, type SmartField_Api_Contracts_Site_SiteDto } from '../../api';
import { useIsAdmin, useIsSiteManager } from '../../lib/auth';
import { SiteFormModal } from './SiteFormModal';
import { SiteLocationMap } from '../../components/SiteLocationMap';

type FormState = {
  isOpen: boolean;
  mode: 'create' | 'edit';
  site?: SmartField_Api_Contracts_Site_SiteDto;
};

export function SitesListPage() {
  const queryClient = useQueryClient();
  const isAdmin = useIsAdmin();
  const isSiteManager = useIsSiteManager();
  const navigate = useNavigate();
  const [formState, setFormState] = useState<FormState>({ isOpen: false, mode: 'create' });
  const [searchQuery, setSearchQuery] = useState('');

  const { data: sites, isLoading } = useQuery({
    queryKey: ['sites'],
    queryFn: () => siteGetSites(),
  });

  const createMutation = useMutation({
    mutationFn: (data: SmartField_Api_Contracts_Site_SiteDto) => 
      siteCreateSite({ requestBody: data }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sites'] });
      setFormState({ isOpen: false, mode: 'create' });
      toast.success('Site created successfully');
    },
    onError: (error) => {
      toast.error(`Failed to create site: ${error.message}`);
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: SmartField_Api_Contracts_Site_SiteDto) => 
      siteUpdateSite({ 
        id: data.id!, 
        requestBody: {
          id: data.id,
          name: data.name,
          location: data.location,
          shapeCoordinates: data.shapeCoordinates,
          startedAtUtc: data.startedAtUtc,
          stoppedAtUtc: data.stoppedAtUtc,
        }
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sites'] });
      setFormState({ isOpen: false, mode: 'create' });
      toast.success('Site updated successfully');
    },
    onError: (error) => {
      toast.error(`Failed to update site: ${error.message}`);
    },
  });

  const filteredSites = sites?.filter(site => 
    site.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    site.serialNumber?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const canManageSites = isAdmin || isSiteManager;

  const formatDate = (dateString?: string | null) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const handleOpenCreate = () => {
    setFormState({ isOpen: true, mode: 'create' });
  };

  const handleOpenEdit = (site: SmartField_Api_Contracts_Site_SiteDto) => {
    setFormState({ isOpen: true, mode: 'edit', site });
  };

  const handleClose = () => {
    setFormState({ isOpen: false, mode: 'create' });
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Sites</h1>
          <p className="mt-2 text-sm text-gray-600">
            Manage field sites and their configurations
          </p>
        </div>
        {canManageSites && (
          <button
            onClick={handleOpenCreate}
            className="px-4 py-2 bg-smartfield text-white rounded-lg hover:bg-smartfield/90 transition-colors font-medium"
          >
            Create Site
          </button>
        )}
      </div>

      <div className="bg-white rounded-lg shadow p-4">
        <input
          type="text"
          placeholder="Search sites by name or serial number..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-smartfield focus:border-transparent bg-white text-gray-900"
        />
      </div>

      {isLoading ? (
        <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">Loading sites...</div>
      ) : filteredSites && filteredSites.length > 0 ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {filteredSites.map((site) => (
            <div key={site.id} className="bg-white rounded-lg shadow hover:shadow-lg transition-shadow overflow-hidden">
              <Link to={`/sites/${site.id}`} className="block">
                {site.location && site.location.y && site.location.x && (
                  <SiteLocationMap
                    latitude={site.location.y}
                    longitude={site.location.x}
                    className="h-64"
                  />
                )}

                <div className="p-6">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="text-xl font-bold text-gray-900">{site.name}</h3>
                      <p className="text-sm text-gray-600 mt-1">{site.serialNumber}</p>
                    </div>
                    <div className="flex gap-2 items-center">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                        site.siteType === 'Stationary'
                          ? 'bg-blue-100 text-blue-800'
                          : 'bg-purple-100 text-purple-800'
                      }`}>
                        {site.siteType}
                      </span>
                      {site.stoppedAtUtc ? (
                        <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800">
                          Stopped
                        </span>
                      ) : (
                        <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
                          Active
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="space-y-2 text-sm">
                    {site.location && site.location.y && site.location.x && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Location:</span>
                        <span className="font-medium text-gray-900">
                          {site.location.y.toFixed(4)}°, {site.location.x.toFixed(4)}°
                        </span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span className="text-gray-600">Started:</span>
                      <span className="font-medium text-gray-900">{formatDate(site.startedAtUtc)}</span>
                    </div>
                    {site.stoppedAtUtc && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Stopped:</span>
                        <span className="font-medium text-gray-900">{formatDate(site.stoppedAtUtc)}</span>
                      </div>
                    )}
                  </div>
                </div>
              </Link>
              {canManageSites && (
                <div className="px-6 pb-6 flex gap-2">
                  <button
                    type="button"
                    onClick={() => handleOpenEdit(site)}
                    className="flex-1 px-4 py-2 bg-smartfield text-white rounded-lg hover:bg-smartfield/90 transition-colors font-medium text-sm"
                  >
                    Edit Site
                  </button>
                  <button
                    type="button"
                    onClick={() => navigate(`/sites/${site.id}/geometry`)}
                    className="px-4 py-2 border border-smartfield text-smartfield rounded-lg hover:bg-smartfield/5 transition-colors font-medium text-sm"
                  >
                    Geometry
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
          {searchQuery ? 'No sites match your search' : 'No sites found'}
        </div>
      )}

      {formState.isOpen && (
        <SiteFormModal
          mode={formState.mode}
          site={formState.site}
          onClose={handleClose}
          onSubmit={formState.mode === 'create' ? createMutation.mutate : updateMutation.mutate}
          isSubmitting={createMutation.isPending || updateMutation.isPending}
          error={createMutation.error || updateMutation.error}
        />
      )}
    </div>
  );
}
